import { formatDate } from '@angular/common';
import { Component, EventEmitter, Injector, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannel,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatConversation,
  ChatMessage,
  ChatMessageTypeEnum,
  ChatSavedMessage,
  ChatSocketService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';
import { chatFileIcon, formatChatFileSize } from '../../chat-composer/chat-attachment.util';
import { ChatPresenceView } from '../../chat-status/chat-status.util';
import { conversationsSelector, presenceViewsSelector, savedLoadingSelector, savedSelector, userDisplaySelector } from '../../state/chat.selectors';
import { resolveChatInitials } from '../../chat-initials.util';

/** Jump target emitted to the host page (opens + highlights the original). */
export interface ChatSavedJump {
  channelId: string;
  messageId: string;
}

/** Precomputed saved row. */
interface ChatSavedRow {
  messageId: string;
  channelId: string;
  channelName: string;
  channelIcon: string;
  authorId: string;
  authorName: string;
  authorInitial: string;
  authorImage?: string;
  timeLabel: string;
  content: string;
  isMedia: boolean;
  fileName?: string;
  fileIcon?: string;
  fileSizeLabel?: string;
}

/**
 * Internal Chat saved-messages view (SPEC-2026-07-internal-chat, T-20 ·
 * RF-14/AC-12).
 *
 * Personal bookmark list read from `GET /api/chat/saved` through the store
 * (membership-scoped server-side: a bookmark from a conversation I left stops
 * being returned). Each row jumps to the original in its conversation or drops
 * the bookmark with `SAVED_TOGGLE` — invisible to every other member.
 */
@Component({
  selector: 'chat-saved',
  templateUrl: './chat-saved.component.html',
  styleUrls: ['./chat-saved.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatSavedComponent extends AbstractComponent implements OnInit {

  /** Row clicked — the host opens the conversation and highlights the message. */
  @Output() jump = new EventEmitter<ChatSavedJump>();

  rows: ChatSavedRow[] = [];
  loading$ = this.store.select(savedLoadingSelector);

  /** userId → presence view (dot class + tooltip parts). */
  private presenceViews: Record<string, ChatPresenceView> = {};

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(savedSelector),
      this.store.select(userDisplaySelector),
      this.store.select(conversationsSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([saved, userDisplay, conversations]) => {
        this.rows = saved
          .map((entry) => this.buildRow(entry, userDisplay, conversations))
          .filter((row): row is ChatSavedRow => !!row);
      });

    // Presence + custom status feed the dot/tooltip on each author tile,
    // exactly like the conversation pane.
    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => this.presenceViews = views);

    // Always refresh on entry — bookmarks change from any surface.
    this.store.dispatch(ChatActions.loadSaved());
  }

  refresh(): void {
    this.store.dispatch(ChatActions.loadSaved());
  }

  open(row: ChatSavedRow): void {
    this.jump.emit({ channelId: row.channelId, messageId: row.messageId });
  }

  unsave(row: ChatSavedRow): void {
    // Optimistic: the row disappears at once (personal state, no fan-out).
    this.store.dispatch(ChatActions.applySavedToggle({ messageId: row.messageId, saved: false }));

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.SAVED_TOGGLE, { messageId: row.messageId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.store.dispatch(ChatActions.loadSaved()),
        error: (ack) => {
          // Restore the bookmark — the server refused the toggle.
          this.store.dispatch(ChatActions.applySavedToggle({ messageId: row.messageId, saved: true }));
          this.store.dispatch(ChatActions.loadSaved());
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  trackRow(_index: number, row: ChatSavedRow): string {
    return row.messageId;
  }

  /** Dot class for the author tile — same semantics as everywhere else. */
  dotFor(row: ChatSavedRow): string {
    return this.presenceViews[row.authorId]?.dotClass ?? 'bg-secondary';
  }

  /** "Nome · 🧉 status" — or the availability label when none is set. */
  authorTooltip(row: ChatSavedRow): string {
    const view = this.presenceViews[row.authorId];
    if (!view) return row.authorName;
    if (view.hasCustomStatus) {
      const emoji = view.emoji ? `${view.emoji} ` : '';
      return `${row.authorName} · ${emoji}${view.text}`.trim();
    }
    return `${row.authorName} · ${this.t(view.labelKey)}`;
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  /**
   * `GET /api/chat/saved` populates `message`/`channel` (same-database populate)
   * and drops entries whose message or channel is removed — an unpopulated
   * (string) ref therefore means the row is not renderable and is skipped.
   */
  private buildRow(
    entry: ChatSavedMessage,
    userDisplay: ChatUserDisplayMap,
    conversations: ChatConversation[],
  ): ChatSavedRow | null {
    const message = typeof entry.message === 'string' ? null : entry.message as ChatMessage;
    const channel = typeof entry.channel === 'string' ? null : entry.channel as ChatChannel;
    if (!message?._id || !channel?._id) return null;

    const display = userDisplay[String(message.sender)];
    const authorName = display?.name ?? this.t('chat.saved.unknownAuthor');
    const isMedia = message.type !== ChatMessageTypeEnum.TEXT;

    return {
      messageId: String(message._id),
      channelId: String(channel._id),
      channelName: this.resolveChannelName(channel, conversations, userDisplay),
      channelIcon: this.resolveIcon(channel.type),
      authorId: String(message.sender),
      authorName,
      authorInitial: resolveChatInitials(authorName || '?'),
      authorImage: display?.image,
      timeLabel: this.formatTime(message.createdAt),
      content: message.content ?? '',
      isMedia,
      fileName: message.fileName,
      fileIcon: isMedia ? chatFileIcon(message.contentType, message.fileName) : undefined,
      fileSizeLabel: (isMedia && message.fileSize) ? formatChatFileSize(message.fileSize) : undefined,
    };
  }

  /**
   * The populated channel has no roster (`members` only exists on the bootstrap
   * ChatConversation shape) — DIRECT/GROUP titles are composed from the store
   * conversation when available, with a generic label as the fallback.
   */
  private resolveChannelName(
    channel: ChatChannel,
    conversations: ChatConversation[],
    userDisplay: ChatUserDisplayMap,
  ): string {
    if (channel.name) return channel.name;

    const currentUserId = this.getIDCurrentUser();
    const conversation = conversations.find((item) => item._id === String(channel._id));
    const memberIds = (conversation?.members ?? []).filter((id) => id !== currentUserId);

    if (channel.type === ChatChannelTypeEnum.DIRECT) {
      const otherId = memberIds[0];
      return (otherId ? userDisplay[otherId]?.name : undefined) ?? this.t('chat.bubble.directConversation');
    }
    if (channel.type === ChatChannelTypeEnum.GROUP) {
      const names = memberIds
        .map((id) => userDisplay[id]?.name)
        .filter((name): name is string => !!name);
      return names.length > 0 ? names.slice(0, 3).join(', ') : this.t('chat.bubble.groupConversation');
    }
    return this.t('chat.bubble.channelConversation');
  }

  private resolveIcon(type: ChatChannelTypeEnum): string {
    switch (type) {
      case ChatChannelTypeEnum.DIRECT: return 'ri-user-3-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      default: return 'ri-hashtag';
    }
  }

  /** Numeric-only formats — locale-neutral (sidebar precedent). */
  private formatTime(value?: Date | string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    if (date.toDateString() === now.toDateString()) return formatDate(date, 'HH:mm', 'en-US');
    if (date.getFullYear() === now.getFullYear()) return formatDate(date, 'dd/MM HH:mm', 'en-US');
    return formatDate(date, 'dd/MM/yy HH:mm', 'en-US');
  }

  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
